import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { getFeeHeadWisePaidList } from "../../api/feeApi";
import "../Attendance/Attendance.css";

const academicYearApi = createLookupApi("academic-years");
const feeHeadApi = createLookupApi("fee-heads");

/**
 * Direct port of digicampusbd's `fee-report-feehead-month-wise-paid-list`
 * ("Paid list Fee Head Wise") — for one chosen fee head, each real student's
 * amount actually paid, broken down by the calendar month that fee head's
 * subheads are scheduled for (per `FeeSubHeadTimeConfiguration`), summed
 * from real payment records. New backend method mirroring the month-wise
 * due report's own approach — see `FeeReportService`'s doc comment.
 */
export default function FeeHeadWisePaidListPage() {
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [feeHeads, setFeeHeads] = useState([]);
  const [feeHeadId, setFeeHeadId] = useState("");

  const [rows, setRows] = useState(null);
  const [monthKeys, setMonthKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
    feeHeadApi.list().then((heads) => {
      setFeeHeads(heads);
      if (heads.length > 0) setFeeHeadId(String(heads[0].id));
    });
  }, []);

  const handleSearch = async () => {
    setError("");
    setRows(null);
    if (!academicYear || !classConfigId || !feeHeadId) {
      setError("Please select academic year, section, and fee head.");
      return;
    }
    setLoading(true);
    try {
      const data = await getFeeHeadWisePaidList(academicYear, classConfigId, feeHeadId);
      const withTotals = data.filter((r) => r.totalPaidAmount !== 0);
      if (withTotals.length === 0) {
        setError("No Paid Found");
      }
      const keys = withTotals.length > 0 ? Object.keys(withTotals[0].paidDetails) : [];
      setMonthKeys(keys);
      setRows(withTotals);
    } catch (err) {
      setError("Failed to load paid list.");
    } finally {
      setLoading(false);
    }
  };

  const grandTotal = (rows || []).reduce((sum, r) => sum + r.totalPaidAmount, 0);

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Paid List Fee Head Wise</h1>
      </div>

      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}

      <div className="attendance-filters">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Select Section
          <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Select Fee Head
          <select value={feeHeadId} onChange={(e) => setFeeHeadId(e.target.value)}>
            {feeHeads.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        rows && (
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Student ID</th>
                  <th>Student Name</th>
                  <th>Roll</th>
                  {monthKeys.map((m) => (
                    <th key={m}>{m}</th>
                  ))}
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.identificationId}>
                    <td>{r.customStudentId}</td>
                    <td>{r.studentName}</td>
                    <td>{r.studentRoll}</td>
                    {monthKeys.map((m) => (
                      <td key={m}>{Math.round(r.paidDetails[m])}</td>
                    ))}
                    <td>{Math.round(r.totalPaidAmount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3 + monthKeys.length} style={{ fontWeight: 800, textAlign: "right" }}>
                    Grand Total
                  </td>
                  <td>{Math.round(grandTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}
    </MainLayout>
  );
}
